import React, { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  Platform,
  Modal,
  ActivityIndicator,
  Dimensions,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { StorefrontCustomization } from "../components/StorefrontCustomization";
import { ProductManagement } from "../components/ProductManagement";
import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { LoadingState } from "../components/ui/LoadingState";
import { CompactStorefrontCustomization } from "../components/vendors/CompactStorefrontCustomization";
import { useMyProducts } from "../hooks/useMyProducts";
import { platformAlert } from "../utils/platformAlert";
import { useToast } from "../contexts/ToastContext";
import type { Product } from "../types/supabase";
import { useVendorProducts } from "../hooks/useVendorProducts";
import { Button } from "../components/ui/Button";
import { ProductCard } from "../components/products/ProductCard";
import { ProductGrid } from "../components/products/ProductGrid";
import { useNavigation, useIsFocused, useFocusEffect } from "@react-navigation/native";
import { StripeVerificationModal } from "../components/ui/StripeVerificationModal";
import { Feather, Ionicons } from "@expo/vector-icons";
import { VendorRatingsModal } from "../components/vendors/VendorRatingsModal";
import { useVendorRatings } from "../hooks/useVendorRatings";
import { setupExpressAccount, getExpressLoginLink } from "../services/paymentService";
import { registerCacheClearFunction } from "../utils/authCacheClear";

// Conditionally import WebView only for native
let WebView: any = null;
if (Platform.OS !== 'web') {
  WebView = require('react-native-webview').WebView;
}

// Module-level cache to persist vendor data across remounts
// This prevents flash when navigating back to this screen
// IMPORTANT: Keyed by user_id to prevent cross-user data leakage
let cachedVendor: any = null;
let cachedVendorUserId: string | null = null;

// Clear cache function - called on auth state changes to prevent cross-user data leakage
function clearProductListingCache(): void {
  console.log('[ProductListingScreen] Clearing module-level cache');
  cachedVendor = null;
  cachedVendorUserId = null;
}

// Register with central auth cache clearing system
registerCacheClearFunction(clearProductListingCache);

export default function ProductListingScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const queryClient = useQueryClient();
  const [forceRefresh, setForceRefresh] = useState(0);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showRatingsModal, setShowRatingsModal] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState<number | null>(null);
  const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
  const [stripeModalMode, setStripeModalMode] = useState<'onboarding' | 'manage'>('onboarding');
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const isFocused = useIsFocused();

  const { showToast } = useToast();

  // Get current session to know the current user ID
  const { data: sessionData, isLoading: isSessionLoading } = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      console.log('[ProductListingScreen] Fetching session...');
      const { data: { session } } = await supabase.auth.getSession();
      console.log('[ProductListingScreen] Session fetched:', session?.user?.id || 'no user');
      return session;
    },
    staleTime: 5 * 1000, // Reduced to 5 seconds to ensure fresh session after sign-in
    refetchOnMount: 'always', // Always refetch when component mounts
  });
  const currentUserId = sessionData?.user?.id;

  const {
    data: vendor,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["vendor-check", currentUserId, forceRefresh],
    enabled: !!currentUserId, // Don't run until we have a user ID
    queryFn: async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) return null;

      // Get the most recent vendor for this user (excluding deleted vendors)
      const { data, error } = await supabase
        .from("vendors")
        .select("*")
        .eq("user_id", session.user.id)
        .neq("status", "deleted")
        .not("is_deleted", "eq", true)
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) {
        console.error("Error fetching vendor:", error);
        return null;
      }

      if (!data || data.length === 0) return null;
      return data[0];
    },
    staleTime: 0,
    gcTime: 0,
    retry: 2,
    retryDelay: 1000,
  });

  // Use useMyProducts for the main product display (filters out 0 quantity)
  const { data: products, refetch: refetchProducts } = useMyProducts();

  // Use useVendorProducts to get count of ALL products for stats
  const { data: allVendorProducts, refetch: refetchVendorProducts } =
    useVendorProducts();

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    console.log("[ProductListingScreen] Pull-to-refresh triggered");

    try {
      // Refetch all data in parallel
      const refreshPromises = [refetch()];

      if (refetchProducts) {
        refreshPromises.push(refetchProducts());
      }

      if (refetchVendorProducts) {
        refreshPromises.push(refetchVendorProducts());
      }

      await Promise.all(refreshPromises);

      console.log(
        "[ProductListingScreen] Pull-to-refresh completed successfully"
      );
    } catch (error) {
      console.error(
        "[ProductListingScreen] Error during pull-to-refresh:",
        error
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const navigation = useNavigation();
  
  // Update module-level cache when we have vendor data
  // Also clear cache if stripe_account_status has changed (e.g., after verification)
  // CRITICAL: Only use cached vendor if it belongs to the SAME user to prevent cross-user data leakage
  if (vendor) {
    // If stripe status changed, clear the old cache to ensure fresh data is used
    if (cachedVendor && cachedVendor.stripe_account_status !== vendor.stripe_account_status) {
      console.log('[ProductListingScreen] Stripe status changed, updating cache:', 
        cachedVendor.stripe_account_status, '->', vendor.stripe_account_status);
    }
    cachedVendor = vendor;
    cachedVendorUserId = vendor.user_id;
  }
  
  // Clear cache if user changed (prevents cross-user data leakage after account deletion/switch)
  if (currentUserId && cachedVendorUserId && currentUserId !== cachedVendorUserId) {
    console.log('[ProductListingScreen] User changed, clearing vendor cache:', 
      cachedVendorUserId, '->', currentUserId);
    cachedVendor = null;
    cachedVendorUserId = null;
  }
  
  // Use cached vendor if current is empty (prevents flash on refetch)
  // BUT only if the cached vendor belongs to the same user (prevents data leakage across accounts)
  const effectiveVendor = vendor || (cachedVendor && cachedVendorUserId === currentUserId ? cachedVendor : null);
  
  // Check Stripe status function (same as VendorSettings)
  const checkStripeStatus = useCallback(async () => {
    if (!effectiveVendor?.id) return;

    // optional: simple cooldown (e.g. 30s)
    const now = Date.now();
    if (lastCheckedAt && now - lastCheckedAt < 30_000) return;
    setLastCheckedAt(now);

    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      if (!token) throw new Error("No session token");

      const { supabaseUrl } = supabase as any;
      const resp = await fetch(
        `${supabaseUrl}/functions/v1/syncStripeAccount`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ vendorId: effectiveVendor.id }),
        }
      );

      const json = await resp.json().catch(() => null);
      console.log("[ProductListingScreen] syncStripeAccount ->", resp.status, json);
      if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);

      // If the server says nothing changed, don't refetch
      if (!json.updated) {
        return;
      }

      // Only refetch when row was actually updated
      await refetch();
      
      // Invalidate vendor-check query to ensure updated stripe_account_status is reflected
      queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
    } catch (e) {
      console.error("[ProductListingScreen] Stripe status sync failed:", e);
    }
  }, [effectiveVendor?.id, lastCheckedAt, refetch, queryClient]);

  // Handle product press - check inventory first
  const handleProductPress = useCallback(async (product: Product) => {
    try {
      // Query the current inventory from the database
      const { data, error } = await supabase
        .from('products')
        .select('quantity')
        .eq('id', product.id)
        .single();

      if (error) {
        console.error('[ProductListingScreen] Error checking inventory:', error);
        // If there's an error, still navigate to the product
        navigation.navigate("AddProduct" as never, { product } as never);
        return;
      }

      const currentQuantity = data?.quantity ?? 0;

      if (currentQuantity <= 0) {
        // Show message that product has been sold
        showToast("You have sold this product.");
        
        // Invalidate queries to refresh the product list and remove this product
        queryClient.invalidateQueries({ queryKey: ['my-products'] });
        queryClient.invalidateQueries({ queryKey: ['vendor-products'] });
        return;
      }

      // Inventory is available, navigate to edit the product
      navigation.navigate("AddProduct" as never, { product } as never);
    } catch (err) {
      console.error('[ProductListingScreen] Error in handleProductPress:', err);
      // On error, navigate anyway
      navigation.navigate("AddProduct" as never, { product } as never);
    }
  }, [navigation, queryClient, showToast]);

  // On web: When component mounts/focuses and vendor has Stripe account with pending status, check status
  // This handles the case where user returns from Stripe onboarding
  useEffect(() => {
    if (Platform.OS !== 'web' || !effectiveVendor?.id || !effectiveVendor?.stripe_accountid) return;
    // Only check if status is pending or not yet active (user might have just completed onboarding)
    if (effectiveVendor.stripe_account_status === 'active') return;
    
    // Check status after a short delay to ensure component is fully mounted
    const timer = setTimeout(async () => {
      console.log('[ProductListingScreen] Auto-checking Stripe status on web mount/focus');
      await checkStripeStatus();
      // Refetch vendor data to get updated status
      await refetch();
      queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [effectiveVendor?.id, effectiveVendor?.stripe_accountid, effectiveVendor?.stripe_account_status, checkStripeStatus, refetch, queryClient, isFocused]);

  // Clear Stripe modal state when screen loses focus
  // This prevents the modal from persisting when navigating away and coming back
  useFocusEffect(
    useCallback(() => {
      // This runs when screen gains focus - nothing to do here
      return () => {
        // This runs when screen loses focus - close the modal
        if (onboardingUrl) {
          console.log('[ProductListingScreen] Screen lost focus, closing Stripe modal');
          setOnboardingUrl(null);
        }
      };
    }, [onboardingUrl])
  );

  // Setup Stripe payment (same as VendorSettings)
  const onSetupPayment = useCallback(async () => {
    if (!effectiveVendor?.id) {
      console.error("[ProductListingScreen] No vendor ID available for Stripe setup");
      return;
    }

    setIsStripeLoading(true);
    setStripeModalMode('onboarding');
    try {
      const url = await setupExpressAccount(effectiveVendor.id);
      setOnboardingUrl(url);
    } catch (err) {
      console.error("[ProductListingScreen] Error initiating Stripe onboarding:", err);
    } finally {
      setIsStripeLoading(false);
    }
  }, [effectiveVendor?.id]);

  // Open Stripe Express dashboard
  const onManageStripe = useCallback(async () => {
    if (!effectiveVendor?.stripe_accountid) return;
    
    setIsStripeLoading(true);
    setStripeModalMode('manage');
    try {
      // Get a login link for the Express dashboard
      const loginUrl = await getExpressLoginLink(effectiveVendor.stripe_accountid);
      setOnboardingUrl(loginUrl);
    } catch (err) {
      console.error("[ProductListingScreen] Error getting Stripe login link:", err);
      // Fallback: try opening in browser
      const dashboardUrl = `https://dashboard.stripe.com/express/${effectiveVendor.stripe_accountid}`;
      if (Platform.OS === 'web') {
        window.open(dashboardUrl, '_blank');
      } else {
        // Import Linking if needed
        const { Linking } = require('react-native');
        Linking.openURL(dashboardUrl);
      }
    } finally {
      setIsStripeLoading(false);
    }
  }, [effectiveVendor?.stripe_accountid]);
  
  // Fetch vendor ratings
  const { data: ratingsData } = useVendorRatings(effectiveVendor?.id);

  // Show loading state while session/vendor data is loading
  // This prevents flashing wrong UI before we know the user's vendor status
  if (isSessionLoading || !currentUserId || (isLoading && !effectiveVendor)) {
    return <LoadingState />;
  }
  if (!effectiveVendor) return null;

  // Check if Stripe is properly set up
  const isStripeActive = effectiveVendor.stripe_account_status === "active";

  const renderHeader = () => (
    <View style={[styles.header]}>
      <View style={styles.topRow}>
        <View style={styles.storeInfoLeft}>
          <Text style={styles.storeName}>{effectiveVendor.store_name}</Text>
        </View>
        <View style={styles.statsRight}>
          <View style={styles.stat}>
            <Text style={styles.statValue}>{products?.length || 0}</Text>
            <Text style={styles.statLabel}>In Stock</Text>
          </View>
          <View style={styles.stat}>
            <Text style={styles.statValue}>{effectiveVendor.total_sales || 0}</Text>
            <Text style={styles.statLabel}>Sales</Text>
          </View>
          <TouchableOpacity 
            style={styles.stat}
            onPress={onManageStripe}
          >
            <Text style={styles.statValue}>
              ${(effectiveVendor.total_revenue || 0).toFixed(0)}
            </Text>
            <Text style={styles.statLabel}>Revenue</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.stat}
            onPress={() => setShowRatingsModal(true)}
          >
            <View style={styles.ratingValue}>
              <Ionicons name="star-outline" size={16} color={colors.primary} />
              <Text style={styles.statValue}>
                {ratingsData?.averageRating ? ratingsData.averageRating.toFixed(1) : '—'}
              </Text>
            </View>
            <Text style={styles.statLabel}>Rating</Text>
          </TouchableOpacity>
        </View>
      </View>
      {effectiveVendor.description ? (
        <Text style={styles.storeDescription}>{effectiveVendor.description}</Text>
      ) : null}
      <View style={styles.storeInfoSection}>
        <CompactStorefrontCustomization vendor={effectiveVendor} />
      </View>
    </View>
  );

  const sections = [
    {
      id: "products",
      title: "",
      component: (
        <>
          <View style={styles.divider} />

          <View style={styles.sectionHeader}>
            <View style={styles.sectionContent}>
              <Text style={styles.sectionTitle}>Products</Text>
            </View>
            <View style={styles.sectionContent}>
              <Button
                onPress={() => navigation.navigate("AddProduct" as never)}
                variant="primary"
                style={styles.addButton}
                textStyle={styles.addButtonText}
                icon="plus"
                size="sm"
                iconSize={14}
              >
                Add Product
              </Button>
            </View>
          </View>

          {!products?.length ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyTitle}>No products listed yet</Text>
              <Text style={styles.emptyText}>
                Start selling by adding your first product
              </Text>
            </View>
          ) : (
            <ProductGrid
              products={products}
              onProductPress={handleProductPress}
              isSellerView={true}
            />
          )}
        </>
      ),
    },
  ];

  // Render Stripe verification screen if not active
  if (!isStripeActive) {
    return (
      <>
        {isStripeLoading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        )}

        {/* -- Onboarding Modal -- */}
        <Modal
          visible={!!onboardingUrl}
          animationType="slide"
          onRequestClose={() => setOnboardingUrl(null)}
        >
          <View style={styles.modalWrapper}>
            <SafeAreaView style={styles.safeArea} edges={["top", "bottom"]}>
              <View style={styles.modalHeader}>
                <Text style={styles.headerText}>Stripe Onboarding</Text>
                <TouchableOpacity
                  onPress={() => setOnboardingUrl(null)}
                  style={styles.closeButton}
                >
                  <Feather name="x" size={24} color={colors.textLight} />
                </TouchableOpacity>
              </View>
              {onboardingUrl && Platform.OS === 'web' ? (
                // Web: Open Stripe in new window (can't use iframe due to CSP)
                <View style={styles.webOnboardingContent}>
                  <Text style={styles.webOnboardingTitle}>Stripe Onboarding</Text>
                  <Text style={styles.webOnboardingText}>
                    Click the button below to open Stripe setup in a new window. Complete the setup there, then return here and click "Done".
                  </Text>
                  <View style={styles.webButtonContainer}>
                    <TouchableOpacity
                      style={styles.webButton}
                      onPress={() => {
                        if (onboardingUrl) {
                          window.open(onboardingUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      <Text style={styles.webButtonText}>Open Stripe Setup</Text>
                    </TouchableOpacity>
                    <View style={{ height: 12 }} />
                    <TouchableOpacity
                      style={styles.webButton}
                      onPress={async (e) => {
                        e?.preventDefault?.();
                        e?.stopPropagation?.();
                        setOnboardingUrl(null);
                        await checkStripeStatus();
                        queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
                        if (effectiveVendor?.stripe_account_status === 'active') {
                          setShowSuccessModal(true);
                        }
                      }}
                    >
                      <Text style={styles.webButtonText}>Done - Check Status</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : onboardingUrl && WebView ? (
                <WebView
                  source={{ uri: onboardingUrl }}
                  style={styles.webview}
                  onNavigationStateChange={(navState: any) => {
                    if (navState.url.includes('onboard-callback')) {
                      setOnboardingUrl(null);
                      checkStripeStatus();
                      queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
                    }
                  }}
                />
              ) : null}
            </SafeAreaView>
          </View>
        </Modal>

        <View style={styles.container}>
          <View style={styles.verificationContainer}>
            <Text style={styles.verificationTitle}>Complete Store Setup</Text>
            <Text style={styles.verificationDescription}>
              Continue to complete your store setup and start selling.
            </Text>

            <View style={styles.verificationStatus}>
              <Text style={styles.statusLabel}>Current Status:</Text>
              <View
                style={[
                  styles.statusBadge,
                  effectiveVendor.stripe_account_status === "pending"
                    ? styles.statusPending
                    : styles.statusNotConnected,
                ]}
              >
                <Text
                  style={[
                    styles.statusText,
                    effectiveVendor.stripe_account_status === "pending"
                      ? styles.statusTextPending
                      : styles.statusTextNotConnected,
                  ]}
                >
                  {effectiveVendor.stripe_account_status === "pending"
                    ? "Setup Pending"
                    : "Not Connected"}
                </Text>
              </View>
            </View>

            <Button
              onPress={() => navigation.navigate('VendorSettings' as never)}
              variant="primary"
              style={styles.setupButton}
            >
              Complete Store Setup
            </Button>
          </View>
        </View>
      </>
    );
  }

  return (
    <>
      {isStripeLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      )}

      {/* -- Stripe Modal (Onboarding or Dashboard) -- */}
      <Modal
        visible={!!onboardingUrl}
        animationType="slide"
        onRequestClose={() => setOnboardingUrl(null)}
      >
        <View style={styles.modalWrapper}>
          <SafeAreaView style={styles.safeArea} edges={["top", "bottom"]}>
            <View style={styles.modalHeader}>
              <Text style={styles.headerText}>
                {stripeModalMode === 'manage' ? 'Stripe Dashboard' : 'Stripe Onboarding'}
              </Text>
              <TouchableOpacity
                onPress={() => setOnboardingUrl(null)}
                style={styles.closeButton}
              >
                <Feather name="x" size={24} color={colors.textLight} />
              </TouchableOpacity>
            </View>
            {onboardingUrl && Platform.OS === 'web' ? (
              // Web: Open Stripe in new window (can't use iframe due to CSP)
              <View style={styles.webOnboardingContent}>
                <Text style={styles.webOnboardingTitle}>
                  {stripeModalMode === 'manage' ? 'Stripe Dashboard' : 'Stripe Onboarding'}
                </Text>
                <Text style={styles.webOnboardingText}>
                  {stripeModalMode === 'manage'
                    ? 'Click the button below to open your Stripe dashboard in a new window. You can manage your bank accounts, view payouts, and more.'
                    : 'Click the button below to open Stripe setup in a new window. Complete the setup there, then return here and click "Done".'
                  }
                </Text>
                <View style={styles.webButtonContainer}>
                  <TouchableOpacity
                    style={styles.webButton}
                    onPress={() => {
                      if (onboardingUrl) {
                        window.open(onboardingUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <Text style={styles.webButtonText}>
                      {stripeModalMode === 'manage' ? 'Open Stripe Dashboard' : 'Open Stripe Setup'}
                    </Text>
                  </TouchableOpacity>
                  <View style={{ height: 12 }} />
                  <TouchableOpacity
                    style={styles.webButton}
                    onPress={async (e) => {
                      e?.preventDefault?.();
                      e?.stopPropagation?.();
                      setOnboardingUrl(null);
                      if (stripeModalMode === 'onboarding') {
                        await checkStripeStatus();
                        queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
                        if (effectiveVendor?.stripe_account_status === 'active') {
                          setShowSuccessModal(true);
                        }
                      }
                    }}
                  >
                    <Text style={styles.webButtonText}>
                      {stripeModalMode === 'manage' ? 'Done' : 'Done - Check Status'}
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : onboardingUrl && WebView ? (
              <WebView
                source={{ uri: onboardingUrl }}
                style={styles.webview}
                onNavigationStateChange={(navState: any) => {
                  if (navState.url.includes('onboard-callback')) {
                    setOnboardingUrl(null);
                    checkStripeStatus();
                    queryClient.invalidateQueries({ queryKey: ["vendor-check"] });
                  }
                }}
              />
            ) : null}
          </SafeAreaView>
        </View>
      </Modal>

      <FlatList
        style={styles.container}
        data={sections}
        renderItem={({ item }) => (
          <View style={styles.section}>
            {item.title ? (
              <View style={styles.sectionContent}>
                <Text style={styles.sectionTitle}>{item.title}</Text>
              </View>
            ) : null}
            {item.component}
          </View>
        )}
        ListHeaderComponent={renderHeader}
        keyExtractor={(item) => item.id}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      />
      <VendorRatingsModal
        visible={showRatingsModal}
        onClose={() => setShowRatingsModal(false)}
        vendorId={effectiveVendor?.id}
        storeName={effectiveVendor?.store_name}
      />
    </>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      paddingVertical: spacing.md,
      backgroundColor: colors.background,
    },
    topRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
      paddingHorizontal: spacing.md,
      marginBottom: spacing.xs,
    },
    storeInfoLeft: {
      flex: 1,
      marginRight: spacing.sm,
    },
    statsRight: {
      flexDirection: "row",
      alignItems: "center",
      gap: spacing.md,
    },
    storeInfoSection: {
      paddingHorizontal: spacing.md,
    },
    storeName: {
      fontSize: 28,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
      marginBottom: 2,
    },
    storeDescription: {
      ...typography.body,
      color: colors.textLight,
      paddingHorizontal: spacing.md,
      marginBottom: spacing.sm,
      fontSize: 15,
      lineHeight: 20,
    },
    divider: {
      height: 1,
      backgroundColor: colors.border,
      marginVertical: spacing.md,
    },
    stats: {
      paddingHorizontal: spacing.md,
    },
    statRow: {
      flexDirection: "row",
      justifyContent: "space-between",
    },
    stat: {
      alignItems: "center",
      justifyContent: "center",
    },
    statValue: {
      fontSize: 18,
      fontWeight: "700",
      color: colors.primary,
      marginBottom: 2,
    },
    statLabel: {
      fontSize: 10,
      fontWeight: "600",
      color: colors.textLight,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    ratingValue: {
      flexDirection: "row",
      alignItems: "center",
      gap: 2,
    },
    analyticsTitle: {
      ...typography.title,
      fontSize: 20,
      fontWeight: "700",
      color: colors.text,
      marginBottom: spacing.md,
    },
    section: {
      borderBottomWidth: 0,
    },
    sectionContent: {
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
    },
    sectionHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingHorizontal: spacing.md,
      marginTop: spacing.lg,
      marginBottom: spacing.sm,
    },
    sectionTitle: {
      fontSize: 22,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    emptyState: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      padding: spacing.xl,
      marginTop: 40,
    },
    emptyTitle: {
      fontSize: 20,
      fontWeight: "700",
      marginBottom: spacing.xs,
      color: colors.text,
    },
    emptyText: {
      ...typography.body,
      color: colors.textLight,
      textAlign: 'center',
      lineHeight: 20,
    },
    addButton: {
      paddingHorizontal: spacing.md,
      height: 36,
      borderRadius: 18,
    },
    addButtonText: {
      fontSize: 14,
      fontWeight: "600",
    },
    // Stripe Verification Styles
    verificationContainer: {
      flex: 1,
      padding: spacing.xl,
      justifyContent: "center",
      alignItems: "center",
    },
    verificationTitle: {
      ...typography.title,
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      textAlign: "center",
      marginBottom: spacing.md,
    },
    verificationDescription: {
      ...typography.body,
      color: colors.textSecondary,
      textAlign: "center",
      lineHeight: 22,
      marginBottom: spacing.xl,
    },
    verificationSteps: {
      width: "100%",
      marginBottom: spacing.xl,
    },
    stepsTitle: {
      ...typography.subtitle,
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    step: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.sm,
    },
    stepBullet: {
      ...typography.body,
      color: colors.primary,
      fontSize: 18,
      marginRight: spacing.sm,
    },
    stepText: {
      ...typography.body,
      color: colors.text,
      flex: 1,
    },
    verificationStatus: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.xl,
    },
    statusLabel: {
      ...typography.body,
      color: colors.text,
      marginRight: spacing.sm,
    },
    statusBadge: {
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.xs,
      borderRadius: spacing.sm,
    },
    statusPending: {
      backgroundColor: colors.backgroundSecondary,
    },
    statusNotConnected: {
      backgroundColor: colors.backgroundSecondary,
    },
    statusText: {
      ...typography.caption,
      fontSize: 12,
      fontWeight: "600",
    },
    statusTextPending: {
      color: colors.warning,
    },
    statusTextNotConnected: {
      color: colors.error,
    },
    setupButton: {
      width: "100%",
      marginTop: spacing.md,
    },
    // Modal styles
    loadingOverlay: {
      ...StyleSheet.absoluteFillObject,
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
    safeArea: { 
      flex: 1, 
      backgroundColor: colors.background 
    },
    modalWrapper: {
      flex: 1,
      ...(Platform.OS === 'web' ? {
        maxWidth: 500,
        alignSelf: 'center',
        width: '100%',
      } : {}),
    },
    modalHeader: {
      paddingHorizontal: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      paddingTop: Platform.OS === 'web' ? spacing.md : 60,
      paddingBottom: spacing.sm,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    headerText: {
      fontSize: 24,
      fontWeight: "700",
      color: colors.text,
    },
    closeButton: {
      padding: spacing.xs,
    },
    webOnboardingContent: {
      flex: 1,
      padding: spacing.xl,
      alignItems: 'center',
      justifyContent: 'center',
    },
    webOnboardingTitle: {
      color: colors.text,
      fontSize: 20,
      fontWeight: '600',
      marginBottom: spacing.md,
      textAlign: 'center',
    },
    webOnboardingText: {
      color: colors.textLight,
      textAlign: 'center',
      marginBottom: spacing.xl,
      paddingHorizontal: spacing.md,
      lineHeight: 22,
    },
    webButtonContainer: {
      width: '100%',
      maxWidth: 300,
    },
    webButton: {
      backgroundColor: colors.primary,
      paddingVertical: spacing.md,
      paddingHorizontal: spacing.lg,
      borderRadius: spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
    },
    webButtonText: {
      color: colors.white || '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
    },
    webview: { 
      flex: 1, 
      height: Dimensions.get('window').height - 64 
    },
  });
